/**
 * Generates CREATE TABLE DDL from @livestore/livestore's State.SQLite.table() column defs
 * (schema.ts's `tables` export), so the real SQLite schema is derived from the same single
 * source of truth the event materializers already use -- no separate DDL to keep in sync by hand.
 */
import type { tables as TablesType } from '@/livestore/schema.js';

type ColumnDef = {
  columnType: 'text' | 'integer' | 'real';
  default: { _tag: 'Some'; value: unknown } | { _tag: 'None' };
  nullable: boolean;
  primaryKey: boolean;
};

/**
 * Extracts `sqliteDef` from a LiveStore table-query-builder object, throwing a clear error
 * when the internal shape doesn't match expectations instead of silently producing `undefined`
 * fields that would lead to missing columns in the generated DDL.
 */
function getSqliteDef(
  table: unknown,
  context: string,
): { columns: Record<string, ColumnDef>; name: string } {
  if (typeof table !== 'object' || table === null || !('sqliteDef' in table)) {
    throw new Error(
      `ddl.ts: table object passed to ${context} is missing 'sqliteDef' — @livestore/livestore's internal shape may have changed`,
    );
  }
  return (table as { sqliteDef: { columns: Record<string, ColumnDef>; name: string } }).sqliteDef;
}

function sqlType(columnType: ColumnDef['columnType']): string {
  if (columnType === 'integer') return 'INTEGER';
  if (columnType === 'real') return 'REAL';
  return 'TEXT';
}

function defaultClause(col: ColumnDef): string {
  if (col.default._tag !== 'Some') return '';
  const v = col.default.value;
  // S7781 false positive: /'/g matches single-quote chars for SQL escaping — NOT replaceable with /./g
  const literal = typeof v === 'string' ? `'${v.replaceAll(/'/g, "''")}'` : JSON.stringify(v); // NOSONAR
  return ` DEFAULT ${literal}`;
}

function columnDdl(name: string, col: ColumnDef): string {
  let line = `"${name}" ${sqlType(col.columnType)}`;
  if (col.primaryKey) line += ' PRIMARY KEY';
  else if (!col.nullable) line += ' NOT NULL';
  line += defaultClause(col);
  return line;
}

/** One CREATE TABLE statement per entry in schema.ts's `tables`, plus the append-only events audit log. */
export function buildCreateTableStatements(tables: typeof TablesType): string[] {
  const stmts = Object.values(tables).map(table => {
    const def = getSqliteDef(table, 'buildCreateTableStatements');
    const cols = Object.entries(def.columns).map(([name, col]) => columnDdl(name, col));
    return `CREATE TABLE IF NOT EXISTS "${def.name}" (${cols.join(', ')})`;
  });
  stmts.push(
    `CREATE TABLE IF NOT EXISTS "events" ("id" TEXT PRIMARY KEY, "name" TEXT NOT NULL, "args" TEXT NOT NULL, "ts" INTEGER NOT NULL)`,
  );
  return stmts;
}

/** { tableName -> declared columns } for every table in schema.ts, keyed the same way
 * buildCreateTableStatements reads them -- used by the migration pass below to diff against
 * what actually exists on an already-created on-disk database. */
export function tableColumnDefs(
  tables: typeof TablesType,
): Record<string, Record<string, ColumnDef>> {
  const out: Record<string, Record<string, ColumnDef>> = {};
  for (const table of Object.values(tables)) {
    const def = getSqliteDef(table, 'tableColumnDefs');
    out[def.name] = def.columns;
  }
  return out;
}

/**
 * `CREATE TABLE IF NOT EXISTS` only creates a table the first time -- it silently does nothing
 * for a table that already exists, so adding a new column to schema.ts (as timeEntries.is_deleted/
 * deleted_at/unsealable_locked were) never reaches anyone whose local SQLite database predates
 * that change. Every write touching the new column then throws "table X has no column named Y",
 * caught by the write queue's .catch() and only logged -- invisible from the UI, looks exactly
 * like silent data loss ("I can save a workplace but time entries don't persist").
 *
 * Diffs each table's declared columns (from schema.ts, via tableColumnDefs above) against what
 * `PRAGMA table_info` reports actually exists, and returns `ALTER TABLE ... ADD COLUMN ...` for
 * whatever's missing. SQLite forbids ADD COLUMN with PRIMARY KEY -- that can only mean the table
 * itself is new/empty (CREATE TABLE already added it) or genuinely corrupt, either way not
 * something ALTER TABLE can or should paper over, so those are skipped rather than attempted.
 */
export function buildAddColumnStatements(
  tableName: string,
  existingColumns: ReadonlySet<string>,
  columns: Record<string, ColumnDef>,
): string[] {
  return Object.entries(columns)
    .filter(([name, col]) => !existingColumns.has(name) && !col.primaryKey)
    .map(([name, col]) => `ALTER TABLE "${tableName}" ADD COLUMN ${columnDdl(name, col)}`);
}
